import {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Rnd } from "react-rnd";

import {
  CustomTemplate,
  CustomTemplateField,
  CustomTemplateFieldType,
} from "./customTemplateTypes";

import { useAuth } from "../Authentification/AuthContext";
import { createCustomTemplateApi } from "./customTemplateApi";

export default function CustomTemplateEditor() {
  const [templateName, setTemplateName] = useState("");
  const [backgroundImage, setBackgroundImage] =
    useState<string | null>(null);

  const [originalWidth, setOriginalWidth] = useState(0);
  const [originalHeight, setOriginalHeight] = useState(0);
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedTemplates, setSavedTemplates] = useState<CustomTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  const { token } = useAuth();

    const api = useMemo(
    () => createCustomTemplateApi(token),
    [token]
    );

  const [fields, setFields] = useState<
    CustomTemplateField[]
  >([]);

  const [selectedFieldId, setSelectedFieldId] =
    useState<string | null>(null);

  const editorRef = useRef<HTMLDivElement | null>(null);

  const handleImageUpload = (
  event: ChangeEvent<HTMLInputElement>
) => {
  const file = event.target.files?.[0];

  if (!file) {
    return;
  }

  if (!file.type.startsWith("image/")) {
    alert("Bitte eine Bilddatei auswählen.");
    return;
  }

  setTemplateFile(file);

  const reader = new FileReader();

  reader.onload = () => {
    const result = reader.result;

    if (typeof result !== "string") {
      return;
    }

    const img = new Image();

    img.onload = () => {
      setOriginalWidth(img.naturalWidth);
      setOriginalHeight(img.naturalHeight);

      // Nur für die lokale Vorschau
      setBackgroundImage(result);

      setFields([]);
      setSelectedFieldId(null);
    };

    img.src = result;
  };

  reader.readAsDataURL(file);
};

    const loadSavedTemplates = async () => {
  try {
    setIsLoadingTemplates(true);

    const templates = await api.getAll();

    setSavedTemplates(templates);
  } catch (error) {
    console.error(
      "Templates konnten nicht geladen werden:",
      error
    );
  } finally {
    setIsLoadingTemplates(false);
  }
};

useEffect(() => {
  loadSavedTemplates();
}, [api]);

  const addField = (
    type: CustomTemplateFieldType
  ) => {
    if (!backgroundImage) {
      alert("Bitte zuerst ein Template hochladen.");
      return;
    }

    const newField: CustomTemplateField = {
        id: crypto.randomUUID(),
        type,
        x: 0.1,
        y: 0.1,
        width: type === "text" ? 0.5 : 0.3,
        height: type === "text" ? 0.12 : 0.3,

        name:
            type === "text"
            ? `Textfeld ${
                fields.filter(
                    (f) => f.type === "text"
                ).length + 1
                }`
            : `Bildfeld ${
                fields.filter(
                    (f) => f.type === "image"
                ).length + 1
                }`,

        fontFamily: type === "text" ? "Barlow" : undefined,

        text:
            type === "text"
            ? ""
            : undefined,
        
        fontSize:
            type === "text"
                ? 32
                : undefined,

            color:
            type === "text"
                ? "#000000"
                : undefined,

            textAlign:
            type === "text"
                ? "left"
                : undefined,
                };
        

    setFields((previous) => [
      ...previous,
      newField,
    ]);

    setSelectedFieldId(newField.id);
  };

  const updateField = (
    fieldId: string,
    changes: Partial<CustomTemplateField>
  ) => {
    setFields((previous) =>
      previous.map((field) =>
        field.id === fieldId
          ? {
              ...field,
              ...changes,
            }
          : field
      )
    );
  };

  const deleteSelectedField = () => {
    if (!selectedFieldId) {
      return;
    }

    setFields((previous) =>
      previous.filter(
        (field) => field.id !== selectedFieldId
      )
    );

    setSelectedFieldId(null);
  };

  const handleSave = async () => {
  if (!templateName.trim()) {
    alert("Bitte einen Template-Namen vergeben.");
    return;
  }

  if (!templateFile) {
    alert("Bitte ein Template hochladen.");
    return;
  }

  try {
    setIsSaving(true);
    setSaveError(null);

    const savedTemplate = await api.create({
      name: templateName.trim(),
      image: templateFile,
      width: originalWidth,
      height: originalHeight,
      fields,
    });

    await loadSavedTemplates();

    console.log(
      "Custom Template gespeichert:",
      savedTemplate
    );

    alert("Template wurde gespeichert.");
  } catch (error) {
    console.error(error);

    setSaveError(
      error instanceof Error
        ? error.message
        : "Template konnte nicht gespeichert werden."
    );
  } finally {
    setIsSaving(false);
  }
};

const handleDeleteTemplate = async (templateId: string) => {
  const confirmed = window.confirm(
    "Soll dieses Template wirklich gelöscht werden?"
  );

  if (!confirmed) {
    return;
  }

  try {
    await api.remove(templateId);

    setSavedTemplates((previous) =>
      previous.filter(
        (template) => template.id !== templateId
      )
    );
  } catch (error) {
    console.error(
      "Template konnte nicht gelöscht werden:",
      error
    );

    alert(
      "Template konnte nicht gelöscht werden."
    );
  }
};

  const editorWidth =
    editorRef.current?.clientWidth ?? 600;

  const aspectRatio =
    originalWidth > 0 && originalHeight > 0
      ? originalHeight / originalWidth
      : 1;

  const editorHeight =
    editorWidth * aspectRatio;

    const previewScale =
    originalWidth > 0
        ? editorWidth / originalWidth
        : 1;

  return (
    <div
      style={{
        maxWidth: 900,
        margin: "0 auto",
      }}
    >
      <h2>Eigenes Template erstellen</h2>

      <div
        style={{
          marginBottom: 20,
        }}
      >
        <label>
          Template-Name
        </label>

        <input
          type="text"
          value={templateName}
          onChange={(event) =>
            setTemplateName(event.target.value)
          }
          placeholder="z. B. GameDays Speaker"
          style={{
            display: "block",
            width: "100%",
            marginTop: 6,
            padding: 8,
          }}
        />
        
      </div>
      

            <div
                style={{
                marginBottom: 20,
                }}
            >
                <label
        style={{
            display: "block",
            marginBottom: 6,
        }}
        >
        Template-Bild
        </label>

        <div
    style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginTop: 6,
        flexWrap: "wrap",
    }}
>
    <label
        style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "10px 16px",
            background: "#008f83",
            color: "#fff",
            borderRadius: 6,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 14,
        }}
    >
        Template-Bild auswählen

        <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleImageUpload}
            style={{
                display: "none",
            }}
        />
    </label>

    <span
        style={{
            fontSize: 14,
            color: "#667085",
        }}
    >
        {templateFile
            ? templateFile.name
            : "Keine Datei ausgewählt"}
    </span>
</div>

        {!backgroundImage && (
        <p
            style={{
            fontSize: 13,
            color: "#6b7280",
            marginTop: 6,
            }}
        >
            Lade ein Template-Bild hoch, um anschließend
            Text- und Bildfelder festzulegen.
        </p>
        )}
      </div>

      {backgroundImage && (
        <>
          <div
            style={{
              display: "flex",
              gap: 10,
              marginBottom: 15,
            }}
          >
            <button
              type="button"
              className="btn-primary"
              onClick={() =>
                addField("text")
              }
            >
              + Textfeld
            </button>

            <button
              type="button"
              className="btn-primary"
              onClick={() =>
                addField("image")
              }
            >
              + Bildfeld
            </button>

            <button
              type="button"
              className="btn-primary"
              onClick={deleteSelectedField}
              disabled={!selectedFieldId}
            >
              Feld löschen
            </button>
          </div>

          <div
            ref={editorRef}
            style={{
              width: "100%",
              position: "relative",
            }}
          >
            <div
              style={{
                width: "100%",
                height: editorHeight,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <img
                src={backgroundImage}
                alt="Template"
                draggable={false}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  userSelect: "none",
                }}
              />

              {fields.map((field) => {
                const selected =
                  field.id === selectedFieldId;

                return (
                  <Rnd
                    key={field.id}
                    bounds="parent"
                    size={{
                      width:
                        field.width *
                        editorWidth,

                      height:
                        field.height *
                        editorHeight,
                    }}
                    position={{
                      x:
                        field.x *
                        editorWidth,

                      y:
                        field.y *
                        editorHeight,
                    }}
                    onMouseDown={() =>
                      setSelectedFieldId(
                        field.id
                      )
                    }
                    onDragStop={(_, data) => {
                      updateField(
                        field.id,
                        {
                          x:
                            data.x /
                            editorWidth,

                          y:
                            data.y /
                            editorHeight,
                        }
                      );
                    }}
                    onResizeStop={(
                      _event,
                      _direction,
                      ref,
                      _delta,
                      position
                    ) => {
                      updateField(
                        field.id,
                        {
                          x:
                            position.x /
                            editorWidth,

                          y:
                            position.y /
                            editorHeight,

                          width:
                            ref.offsetWidth /
                            editorWidth,

                          height:
                            ref.offsetHeight /
                            editorHeight,
                        }
                      );
                    }}
                    style={{
                      border: selected
                        ? "2px solid #007f7b"
                        : "2px dashed #555",

                      background:
                        field.type === "text"
                          ? "rgba(255,255,255,0.55)"
                          : "rgba(0,127,123,0.25)",

                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        "center",

                      cursor: "move",
                      boxSizing:
                        "border-box",
                    }}
                  >
                    <div
                        style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "flex-start",

                            justifyContent:
                            field.type === "text"
                                ? field.textAlign === "center"
                                ? "center"
                                : field.textAlign === "right"
                                    ? "flex-end"
                                    : "flex-start"
                                : "center",

                            padding: 6,
                            boxSizing: "border-box",
                            overflow: "hidden",
                        }}
                        >
                        <span
                            style={{
                            fontWeight: 600,

                            fontSize:
                            field.type === "text"
                                ? `${(field.fontSize ?? 32) * previewScale}px`
                                : 14,

                            fontFamily:
                            field.type === "text"
                                ? field.fontFamily ?? "Barlow"
                                : undefined,

                            color:
                                field.type === "text"
                                ? field.color ?? "#000000"
                                : undefined,

                            textAlign:
                                field.type === "text"
                                ? field.textAlign ?? "left"
                                : "center",

                            lineHeight: 1.2,

                            width:
                                field.type === "text"
                                ? "100%"
                                : "auto",
                            }}
                        >
                            {field.name}
                        </span>
                        </div>
                  </Rnd>
                );
              })}
            </div>
          </div>

          {selectedFieldId && (
            <FieldSettings
              field={
                fields.find(
                  (field) =>
                    field.id ===
                    selectedFieldId
                )!
              }
              onChange={(changes) =>
                updateField(
                  selectedFieldId,
                  changes
                )
              }
            />
          )}

          <button
                type="button"
                className="btn-primary"
                onClick={handleSave}
                disabled={isSaving}
                >
                {isSaving
                    ? "Template wird gespeichert..."
                    : "Template speichern"}
            </button>
                        {saveError && (
            <p style={{ color: "red" }}>
                {saveError}
            </p>
            )}
            <div
  style={{
    marginTop: 30,
  }}
>
  <h3>Gespeicherte Templates</h3>

  {isLoadingTemplates && (
    <p>Templates werden geladen...</p>
  )}

  {!isLoadingTemplates &&
    savedTemplates.length === 0 && (
      <p>
        Noch keine eigenen Templates gespeichert.
      </p>
    )}

  <div
    style={{
      display: "grid",
      gridTemplateColumns:
        "repeat(auto-fill, minmax(160px, 1fr))",
      gap: 16,
    }}
  >
    {savedTemplates.map((template) => (
      <div
        key={template.id}
        style={{
          border: "1px solid #ddd",
          borderRadius: 8,
          padding: 10,
        }}
      >
        <img
          src={template.backgroundImageUrl}
          alt={template.name}
          style={{
            width: "100%",
            height: 120,
            objectFit: "contain",
          }}
        />

        <strong>
          {template.name}
        </strong>

        <div
          style={{
            fontSize: 12,
            marginTop: 5,
          }}
        >
          {template.fields.length} Feld(er)
        </div>
        <button
        type="button"
        onClick={() =>
            handleDeleteTemplate(template.id)
        }
        style={{
            marginTop: 10,
        }}
        >
        Löschen
        </button>
      </div>
    ))}
  </div>
</div>
        </>
      )}
    </div>
  );
}

interface FieldSettingsProps {
  field: CustomTemplateField;

  onChange: (
    changes: Partial<CustomTemplateField>
  ) => void;
}

function FieldSettings({
  field,
  onChange,
}: FieldSettingsProps) {
  return (
    <div
      style={{
        marginTop: 20,
      }}
    >
      <h3>Feldeinstellungen</h3>

      <label>
        Bezeichnung
      </label>

      <input
            type="text"
            value={field.name}
            onChange={(event) =>
            onChange({
                name: event.target.value,
            })
            }
            style={{
            display: "block",
            width: "100%",
            marginTop: 6,
            padding: 8,
            }}
        />

        {field.type === "text" && (
  <>
    <div style={{ marginTop: 12 }}>
      <label>Schriftgröße</label>

      <input
        type="number"
        min={8}
        max={200}
        value={field.fontSize ?? 32}
        onChange={(event) =>
          onChange({
            fontSize:
              Number(event.target.value),
          })
        }
        style={{
          display: "block",
          width: "100%",
          marginTop: 6,
          padding: 8,
        }}
      />
    </div>

    <div style={{ marginTop: 12 }}>
      <label>Textfarbe</label>

      <input
        type="color"
        value={field.color ?? "#000000"}
        onChange={(event) =>
          onChange({
            color: event.target.value,
          })
        }
        style={{
          display: "block",
          marginTop: 6,
        }}
      />
    </div>

    <div style={{ marginTop: 12 }}>
  <label>Schriftart</label>

  <select
    value={field.fontFamily ?? "Barlow"}
    onChange={(event) =>
      onChange({
        fontFamily: event.target.value as "Barlow" | "Cocon",
      })
    }
    style={{
      display: "block",
      width: "100%",
      marginTop: 6,
      padding: 8,
    }}
  >
    <option value="Barlow">Barlow</option>
    <option value="Cocon">Cocon</option>
  </select>
</div>

    <div style={{ marginTop: 12 }}>
      <label>Ausrichtung</label>

      <select
        value={field.textAlign ?? "left"}
        onChange={(event) =>
          onChange({
            textAlign:
              event.target.value as
                | "left"
                | "center"
                | "right",
          })
        }
        style={{
          display: "block",
          width: "100%",
          marginTop: 6,
          padding: 8,
        }}
      >
        <option value="left">
          Links
        </option>

        <option value="center">
          Zentriert
        </option>

        <option value="right">
          Rechts
        </option>
      </select>
    </div>
  </>
)}

      <p>
        Typ:{" "}
        {field.type === "text"
          ? "Textfeld"
          : "Bildfeld"}
      </p>
    </div>
  );
}